/**
 * Google Analytics 4 — consent-gated, SPA-aware page-view tracking.
 *
 * - Reads the GA4 Measurement ID from REACT_APP_GA4_MEASUREMENT_ID (env).
 * - Loads gtag.js ONLY after the user accepts "all" cookies (GDPR/ACCC-friendly).
 * - Tracks every route change as a `page_view` event.
 * - Idempotent — multiple calls won't double-load the script.
 *
 * Mounted once in App.js. Re-listens to the existing
 * `solefinder:cookie-consent` event from <CookieConsent /> so updates take
 * effect immediately when the user toggles preferences.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'solefinder_cookie_consent_v1';

// Read GA4 Measurement ID from env var (preferred) or fall back to the
// <meta name="ga4-measurement-id"> tag in index.html. This double path
// lets us ship the ID without needing a custom Emergent secret.
function getGaId() {
  const fromEnv = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  if (fromEnv) return fromEnv;
  if (typeof document !== 'undefined') {
    const meta = document.querySelector('meta[name="ga4-measurement-id"]');
    if (meta?.content) return meta.content;
  }
  return null;
}

const GA_ID = getGaId();

function getConsent() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return saved?.choice; // 'all' | 'essential' | undefined
  } catch {
    return undefined;
  }
}

function loadGtag() {
  if (!GA_ID || window.__solefinderGaLoaded) return;
  window.__solefinderGaLoaded = true;

  // gtag.js boilerplate (the standard snippet Google docs hand out)
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  // anonymize_ip + SPA mode (we send our own page_view per route change)
  window.gtag('config', GA_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });
}

export default function Analytics() {
  const location = useLocation();
  const lastPath = useRef('');

  // 1. On mount, load gtag if consent already given (returning visitor)
  useEffect(() => {
    if (getConsent() === 'all') loadGtag();

    // 2. Listen for consent change — load on demand
    const onConsent = (e) => {
      if (e?.detail === 'all') loadGtag();
    };
    window.addEventListener('solefinder:cookie-consent', onConsent);
    return () => window.removeEventListener('solefinder:cookie-consent', onConsent);
  }, []);

  // 3. SPA page-view tracking on every route change
  useEffect(() => {
    if (!GA_ID || !window.gtag) return;
    const path = location.pathname + location.search;
    if (path === lastPath.current) return;
    lastPath.current = path;
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);

  return null;
}
